import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcCheck    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcBolt     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
const IcCard     = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
const IcArrowUp  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
const IcEdit     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const IcUpload   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
const IcRefresh  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
const IcExternal = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
const IcX        = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const IcAlert    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>

// ── Helpers ─────────────────────────────────────────────────────────────────
const parseStorageMb = (s) => {
  if (!s) return 1024
  const m = String(s).match(/([\d.]+)\s*(gb|mb)?/i)
  if (!m) return 1024
  const n = parseFloat(m[1])
  return /mb/i.test(m[2] || '') ? n : n * 1024
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

// ── Usage row (prototype ClUsage) ───────────────────────────────────────────
function ClUsage({ label, used, total, unit = '', note, unlimited }) {
  const pct = unlimited
    ? Math.min((used / (used + 50)) * 100, 90)
    : (total > 0 ? Math.min((used / total) * 100, 100) : 0)
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 12, color: 'var(--text-soft)', marginBottom: 5 }}>
        <span style={{ whiteSpace: 'nowrap' }}>{label}{note && <span style={{ color: 'var(--text-faint)' }}> · {note}</span>}</span>
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {used}{unit} / {unlimited ? 'Unlimited' : `${total}${unit}`}
        </span>
      </div>
      <div className="progress thin"><div style={{ width: `${pct}%` }} /></div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const ClientBilling = () => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showCancelModal, setShowCancelModal]   = useState(false)
  const [selectedPlan, setSelectedPlan]         = useState(null)
  const queryClient = useQueryClient()

  // ── Queries ─────────────────────────────────────────────────────────────────
  const { data: billing, isLoading: billingLoading } = useQuery('client-billing', () => apiService.get('/billing').then(r => r.data))
  const { data: profile, isLoading: profileLoading } = useQuery('client-profile', () => apiService.get('/clients/profile').then(r => r.data))
  const { data: plans, isLoading: plansLoading }     = useQuery('billing-plans', () => apiService.get('/billing/plans').then(r => r.data))
  const { data: invoicesData }                       = useQuery('client-invoices', () => apiService.get('/billing/invoices').then(r => r.data))

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const createCheckoutMutation = useMutation(
    (planId) => apiService.post('/billing/create-checkout-session', { planId }).then(r => r.data),
    {
      onSuccess: (data) => { if (data.checkoutUrl) window.location.href = data.checkoutUrl; else { queryClient.invalidateQueries('client-profile'); toast.success('Plan updated') } },
      onError: () => toast.error('Failed to start checkout. Please try again.')
    }
  )

  const portalMutation = useMutation(
    () => apiService.post('/billing/portal').then(r => r.data),
    {
      onSuccess: (data) => { if (data.url) window.location.href = data.url },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not open billing portal')
    }
  )

  const cancelSubscriptionMutation = useMutation(
    () => apiService.post('/billing/cancel'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-billing')
        queryClient.invalidateQueries('client-profile')
        toast.success('Subscription cancelled')
        setShowCancelModal(false)
      },
      onError: () => toast.error('Failed to cancel subscription. Please try again.')
    }
  )

  const handleUpgrade = (planId, plan) => { setSelectedPlan({ id: planId, ...plan }); setShowUpgradeModal(true) }
  const confirmUpgrade = () => { createCheckoutMutation.mutate(selectedPlan.id); setShowUpgradeModal(false) }
  const scrollToPlans = () => document.getElementById('compare-plans')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  if (billingLoading || profileLoading || plansLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading billing information…</div>
  }

  // ── Derived data ──────────────────────────────────────────────────────────────
  const currentPlan = profile?.subscription_plan || 'basic'
  const planData    = plans?.[currentPlan]
  const feats       = planData?.features || {}
  const isFreePlan  = planData?.price === 0
  const isActive    = profile?.billing_status === 'active' || isFreePlan
  const isCancelled = profile?.billing_status === 'canceled' || profile?.billing_status === 'cancelled'
  const price       = planData?.price ?? 0

  const editsUsed  = profile?.edits_used || 0
  const editsTotal = feats.editsPerMonth
  const sitesUsed  = profile?.site_count || 0
  const sitesTotal = feats.sites
  const storageUsed = Math.round((profile?.storage_used_mb || 0))
  const storageTotal = parseStorageMb(feats.storage)

  const invoices = invoicesData?.invoices || []
  const allPaid  = invoices.length > 0 && invoices.every(i => i.status === 'paid')
  const paidToDate = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0)
  const cardLast4 = billing?.card_last_four

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Billing</h1>
          <div className="subtitle">Your plan, payment method and invoices.</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={() => portalMutation.mutate()} disabled={portalMutation.isLoading}>
            <IcUpload /> {portalMutation.isLoading ? 'Opening…' : 'Download all invoices'}
          </button>
        </div>
      </div>

      <div className="page-body" style={{ gap: 18 }}>
        <div className="bento" style={{ gridTemplateColumns: '1.2fr 1fr', gap: 14, alignItems: 'stretch' }}>
          {/* Plan card */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="icon-tile" style={{ width: 36, height: 36, fontSize: 16 }}><IcBolt /></span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{planData?.name || 'Your plan'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isFreePlan ? 'Free plan' : 'Billed monthly'}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>£{price}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}>/mo</span></div>
                {profile?.quota_reset_date && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Next charge {fmtDate(profile.quota_reset_date)}</div>}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <ClUsage label="Sites" used={sitesUsed} total={sitesTotal === 'unlimited' ? sitesUsed : (sitesTotal || 1)} unlimited={sitesTotal === 'unlimited'} />
              <ClUsage label="Edit requests" note="this month" used={editsUsed} total={editsTotal === 'unlimited' ? editsUsed : (editsTotal || 10)} unlimited={editsTotal === 'unlimited'} />
              <ClUsage label="Storage" used={storageUsed} total={storageTotal} unit=" MB" />
            </div>

            <div className="flex gap-2" style={{ marginTop: 18 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={scrollToPlans}><IcArrowUp /> Upgrade plan</button>
              {isActive && !isFreePlan && <button className="btn" onClick={() => setShowCancelModal(true)}>Cancel plan</button>}
            </div>
          </div>

          {/* Payment method + summary */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <h2 className="h2">Payment method</h2>
            <div style={{ marginTop: 14, padding: 18, borderRadius: 14, background: 'linear-gradient(135deg, #2a2150, #1a1530)', border: '1px solid var(--border-strong)', position: 'relative', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ color: 'white' }}><IcCard /></span>
                <span style={{ fontSize: 13, fontWeight: 700, fontStyle: 'italic', color: 'white', letterSpacing: '0.02em' }}>{billing?.payment_method || 'VISA'}</span>
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, color: 'white', marginTop: 22, letterSpacing: '0.12em' }}>
                {cardLast4 ? `•••• •••• •••• ${cardLast4}` : 'No card on file'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 11.5, color: 'rgba(255,255,255,0.7)' }}>
                <span>{(profile?.company_name || 'Account').toUpperCase()}</span>
              </div>
            </div>
            <button className="btn btn-sm" style={{ marginTop: 12, width: '100%' }} onClick={() => portalMutation.mutate()} disabled={portalMutation.isLoading}>
              <IcEdit /> {portalMutation.isLoading ? 'Opening…' : (cardLast4 ? 'Update card' : 'Add a card')}
            </button>

            <div className="divider" style={{ margin: '16px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { l: 'Billing email', v: profile?.email || '—' },
                { l: 'Next invoice', v: isFreePlan ? '—' : `£${price} · ${fmtDate(profile?.quota_reset_date)}` },
                { l: 'Paid to date', v: `£${paidToDate.toFixed(2).replace(/\.00$/, '')}` },
              ].map(row => (
                <div key={row.l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, gap: 12 }}>
                  <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{row.l}</span>
                  <span style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compare plans */}
        <div id="compare-plans">
          <h2 className="h2" style={{ marginBottom: 4 }}>Compare plans</h2>
          <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 14 }}>Switch any time — changes are prorated.</div>
          <div className="bento" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {Object.entries(plans || {}).map(([planId, plan]) => {
              const current = planId === currentPlan
              const f = plan.features || {}
              const featureList = [
                `${f.sites === 'unlimited' ? 'Unlimited' : f.sites || 1} website${f.sites === 1 ? '' : 's'}`,
                `${f.editsPerMonth === 'unlimited' ? 'Unlimited' : f.editsPerMonth || 5} edit requests / mo`,
                `${f.storage || '1GB'} storage`,
                f.support || 'Email support',
              ]
              return (
                <div key={planId} className="card card-pad" style={{ border: current ? '1px solid var(--border-accent)' : '1px solid var(--border)', position: 'relative' }}>
                  {current && <span className="badge badge-accent" style={{ position: 'absolute', top: 16, right: 16 }}>Current</span>}
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{plan.name}</div>
                  <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 8 }}>£{plan.price}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}>/mo</span></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                    {featureList.map(ft => (
                      <div key={ft} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-soft)' }}>
                        <span style={{ color: 'var(--success)', display: 'inline-flex' }}><IcCheck /></span> {ft}
                      </div>
                    ))}
                  </div>
                  <button
                    className={`btn btn-sm ${!current && plan.price > price ? 'btn-primary' : ''}`}
                    style={{ marginTop: 18, width: '100%', justifyContent: 'center' }}
                    disabled={current || createCheckoutMutation.isLoading}
                    onClick={() => handleUpgrade(planId, plan)}
                  >
                    {current ? 'Your plan' : plan.price > price ? 'Upgrade' : 'Downgrade'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Invoice history */}
        <div className="card card-pad">
          <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
            <h2 className="h2">Invoice history</h2>
            {allPaid && <span className="badge badge-success"><IcCheck /> All paid</span>}
          </div>
          {invoices.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 12.5 }}>No invoices yet — they'll appear here after your first payment.</div>
          ) : (
            <div className="sites-table">
              <div className="row header" style={{ gridTemplateColumns: '1.4fr 2fr 0.8fr 0.9fr 1.2fr auto' }}>
                <div>Invoice</div><div>Description</div><div>Amount</div><div>Status</div><div>Date</div><div></div>
              </div>
              {invoices.map((inv, i) => (
                <div className="row" key={inv.id || i} style={{ gridTemplateColumns: '1.4fr 2fr 0.8fr 0.9fr 1.2fr auto' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.id}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.description}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>£{inv.amount}</div>
                  <div><span className={`badge badge-${inv.status === 'paid' ? 'success' : inv.status === 'pending' ? 'warning' : 'danger'}`}><span className="badge-dot" /> {inv.status}</span></div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{fmtDate(inv.date)}</div>
                  <div>
                    {inv.downloadUrl
                      ? <a href={inv.downloadUrl} target="_blank" rel="noopener noreferrer"><button className="btn btn-sm btn-icon" title="Download invoice"><IcUpload /></button></a>
                      : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade / switch modal */}
      {showUpgradeModal && selectedPlan && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={() => setShowUpgradeModal(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 420, zIndex: 1 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="h2">{selectedPlan.price > price ? 'Upgrade plan' : 'Switch plan'}</h2>
              <button className="btn btn-sm btn-icon" onClick={() => setShowUpgradeModal(false)}><IcX /></button>
            </div>
            <div style={{ padding: 16, borderRadius: 12, border: '1px solid var(--border-accent)', background: 'var(--accent-soft)', marginBottom: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{selectedPlan.name}</div>
              <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--accent-3)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>£{selectedPlan.price}/mo</div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16, padding: 12, background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)' }}>
              {selectedPlan.price === 0 ? 'This is a free plan — it activates immediately.' : "You'll be taken to Stripe to confirm. Billing is prorated automatically."}
            </div>
            <div className="flex gap-2">
              <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowUpgradeModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={confirmUpgrade} disabled={createCheckoutMutation.isLoading}>
                {createCheckoutMutation.isLoading ? <><IcRefresh /> Processing…</> : <><IcExternal /> Continue</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)' }} onClick={() => setShowCancelModal(false)} />
          <div className="card card-pad" style={{ position: 'relative', width: '100%', maxWidth: 400, zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span className="icon-tile danger" style={{ width: 36, height: 36 }}><IcAlert /></span>
              <h2 className="h2" style={{ color: 'var(--danger)' }}>Cancel subscription</h2>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
              Are you sure? Your subscription stays active until the end of the billing period, then your account reverts to limited access.
            </div>
            <div className="flex gap-2">
              <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setShowCancelModal(false)}>Keep subscription</button>
              <button className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={() => cancelSubscriptionMutation.mutate()} disabled={cancelSubscriptionMutation.isLoading}>
                {cancelSubscriptionMutation.isLoading ? 'Cancelling…' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ClientBilling
